// Finance DTOs: price book, invoices (multi-case, line items), payments, expenses. Money is
// integer minor units (paisa); rates are bps. The single source of truth for a client's balance
// is DERIVED (Σ non-void invoice totals − Σ verified payments), never a stored ledger.
import { z } from 'zod';
import { CASE_GRADES, type CaseGrade } from './grading.js';

export const PAYMENT_METHODS = [
  'cash',
  'bank_transfer',
  'easypaisa',
  'jazzcash',
  'cheque',
  'card',
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  easypaisa: 'Easypaisa',
  jazzcash: 'JazzCash',
  cheque: 'Cheque',
  card: 'Card',
};

export const INVOICE_STATUSES = ['draft', 'issued', 'partial', 'paid', 'void'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const PAYMENT_STATUSES = ['pending', 'verified', 'void'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PRICE_SOURCES = ['locked', 'pricelist', 'provisional', 'manual', 'remake'] as const;
export type PriceSource = (typeof PRICE_SOURCES)[number];

export interface PriceListItemDto {
  id: string;
  service: string;
  grade: CaseGrade | null;
  unitPrice: number;
  currency: string;
  taxRateCode: string | null;
  isActive: boolean;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaxRateDto {
  code: string;
  name: string;
  rateBps: number;
  isActive: boolean;
}

export interface InvoiceLineDto {
  id: string;
  invoiceId: string;
  caseId: string | null;
  service: string;
  grade: CaseGrade | null;
  description: string | null;
  quantity: number;
  unitPrice: number;
  discountBps: number;
  discountAmount: number;
  lineDiscount: number;
  taxRateBps: number;
  taxAmount: number;
  lineTotal: number;
  priceSource: PriceSource;
  isRemake: boolean;
}

export interface InvoiceDto {
  id: string;
  invoiceNumber: string;
  clientId: string | null;
  clientName: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  deliveryCharges: number;
  totalAmount: number;
  paidAmount: number;
  discountBps: number;
  discountAmount: number;
  status: InvoiceStatus;
  isAuto: boolean;
  notes: string | null;
  lines: InvoiceLineDto[];
  createdAt: string;
  updatedAt: string;
}

export interface PaymentDto {
  id: string;
  paymentNumber: string | null;
  clientId: string | null;
  invoiceId: string | null;
  amount: number;
  method: PaymentMethod;
  reference: string | null;
  notes: string | null;
  paymentDate: string;
  status: PaymentStatus;
  verifiedBy: string | null;
  verifiedAt: string | null;
  createdAt: string;
}

export interface ExpenseDto {
  id: string;
  category: string;
  payee: string | null;
  supplierId: string | null;
  amount: number;
  method: PaymentMethod | null;
  reference: string | null;
  notes: string | null;
  expenseDate: string;
  createdAt: string;
}

/** A client's derived financial position. */
export interface ClientBalanceDto {
  clientId: string | null;
  invoicedTotal: number;
  verifiedPaidTotal: number;
  balance: number;
}

// --- request schemas ---------------------------------------------------------

const money = z.number().int().min(0); // minor units
const bps = z.number().int().min(0).max(10000);

export const manualLineSchema = z.object({
  service: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullish(),
  quantity: z.number().int().min(1).default(1),
  unitPrice: money,
  taxRateCode: z.string().max(20).nullish(),
  discountBps: bps.default(0),
  discountAmount: money.default(0),
});
export type ManualLineRequest = z.infer<typeof manualLineSchema>;

export const createInvoiceSchema = z
  .object({
    clientId: z.string().min(1).nullish(),
    clientName: z.string().trim().min(1).max(160),
    caseIds: z.array(z.string().min(1)).default([]),
    manualLines: z.array(manualLineSchema).default([]),
    discountBps: bps.default(0),
    discountAmount: money.default(0),
    deliveryCharges: money.default(0),
    invoiceDate: z.string().min(1).nullish(),
    status: z.enum(['draft', 'issued']).default('issued'),
    notes: z.string().trim().max(2000).nullish(),
  })
  .refine((v) => v.caseIds.length + v.manualLines.length > 0, { message: 'Need at least one line' })
  .refine((v) => !(v.discountBps > 0 && v.discountAmount > 0), {
    message: 'Use a percent OR a flat discount, not both',
  });
export type CreateInvoiceRequest = z.infer<typeof createInvoiceSchema>;

export const addPaymentSchema = z.object({
  invoiceId: z.string().min(1).nullish(),
  clientId: z.string().min(1).nullish(),
  amount: money.refine((v) => v > 0, { message: 'Amount must be positive' }),
  method: z.enum(PAYMENT_METHODS),
  reference: z.string().trim().max(120).nullish(),
  paymentDate: z.string().min(1).nullish(),
  notes: z.string().trim().max(1000).nullish(),
});
export type AddPaymentRequest = z.infer<typeof addPaymentSchema>;

export const verifyPaymentSchema = z.object({ note: z.string().trim().max(500).nullish() });

export const createExpenseSchema = z.object({
  category: z.string().trim().min(1).max(60),
  payee: z.string().trim().max(160).nullish(),
  supplierId: z.string().min(1).nullish(),
  amount: money.refine((v) => v > 0, { message: 'Amount must be positive' }),
  method: z.enum(PAYMENT_METHODS).nullish(),
  reference: z.string().trim().max(120).nullish(),
  expenseDate: z.string().min(1).nullish(),
  notes: z.string().trim().max(1000).nullish(),
});
export type CreateExpenseRequest = z.infer<typeof createExpenseSchema>;

export const upsertPriceItemSchema = z.object({
  service: z.string().trim().min(1).max(120),
  grade: z.enum(CASE_GRADES).nullish(),
  unitPrice: money,
  taxRateCode: z.string().max(20).nullish(),
  effectiveFrom: z.string().nullish(),
  effectiveTo: z.string().nullish(),
  notes: z.string().trim().max(500).nullish(),
});
export type UpsertPriceItemRequest = z.infer<typeof upsertPriceItemSchema>;

export interface InvoiceListQuery {
  clientId?: string;
  status?: InvoiceStatus;
  limit: number;
  offset: number;
}
